import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { LocationAutocomplete } from "@/components/ui/location-autocomplete";
import { TimezoneDisplay } from "@/components/ui/timezone-display";
import { useForm, useWatch } from "react-hook-form";
import { step3Schema, type Step3FormValues } from "@/schemas/onboarding-schema";
import { useState, useEffect } from "react";
import { Upload, User, Phone, X, Loader2 } from "lucide-react";
import nautikaIcon from "@/assets/img/nautika.svg";
import { AsYouType } from "libphonenumber-js";
import { authClient } from "@/lib/auth-client";
import slugify from "slugify";
import { toast } from "sonner";
import { api } from "@api";
import { useOnboarding } from "@/hooks/useOnboarding";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRightPanel } from "@/hooks/useRightPanel";
import FormShell from "@/components/form-shell";
import { compressImage, isValidImageFile } from "@/lib/image-utils";
import { useGeoLocationInfo } from "@/hooks/useGeoLocation";

// Component for the preview panel
function ClubPreview({ previewData }: { previewData: { fullName: string; phone: string; clubName: string; clubLogo: string | null } }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="bg-club-default p-6">
          <div className="flex justify-between">
            <div className="flex flex-col gap-2">
              <div className="text-xl font-medium text-white italic">Aperçu de votre club</div>
              <p className="text-sm text-orange-950 italic">Votre oasis de détente en bord de mer</p>
            </div>
            <div className="text-gray-400">
              <img src={nautikaIcon} alt="Nautika" className="h-10 w-10 grayscale" />
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start p-6 pt-0">
          <div className="flex flex-col items-center gap-4">
            <div className="-mt-4 mb-4 flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-club-default shadow-lg">
              {previewData.clubLogo ? (
                <img src={previewData.clubLogo} alt="Club logo" className="h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center justify-center text-sm text-white">
                  <div className="text-4xl">
                    {previewData.clubName
                      ? previewData.clubName
                          .match(/\b(\w)/g)
                          ?.join("")
                          .toUpperCase()
                          .slice(0, 4)
                      : "CLUB"}
                  </div>
                </div>
              )}
            </div>
            <div className="mb-6 text-xl">
              <span className="rounded-xl bg-gray-100 px-3 py-1 italic">{previewData.clubName || "Nom du club"}</span>
            </div>
          </div>

          <Separator className="-mt-2 mb-4" />

          <div className="w-full">
            <div className="mb-2 text-lg font-medium">Informations du gérant</div>

            <div className="mb-2 flex items-center">
              <User className="mr-2 h-5 w-5 text-gray-400" />
              <div className={`${previewData.fullName ? "" : "text-gray-300 italic"}`}>{previewData.fullName || "Nom et prénom"}</div>
            </div>

            <div className="flex items-center">
              <Phone className="mr-2 h-5 w-5 text-gray-400" />
              <div className={`${previewData.phone ? "" : "text-gray-300 italic"}`}>{previewData.phone || "+33 6 00 00 00 00"}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OnboardingStep3() {
  const { updateOnboardingStep } = useOnboarding("step3");
  const { setRight } = useRightPanel();
  const { data: geoInfo } = useGeoLocationInfo();

  const [previewData, setPreviewData] = useState<{
    fullName: string;
    phone: string;
    clubName: string;
    clubLogo: string | null;
  }>({
    fullName: "",
    phone: "",
    clubName: "",
    clubLogo: null,
  });

  const [fileName, setFileName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Hook for image upload via Cloudflare R2
  const { mutateAsync: requestUpload } = api.useMutation("post", "/club/image");
  // Hook for phone verification
  const { mutateAsync: checkPhone } = api.useMutation("get", "/onboarding/check-phone/{phone}");

  const form = useForm<Step3FormValues>({
    resolver: zodResolver(step3Schema), // Ajout du resolver Zod
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      lastName: "",
      firstName: "",
      phone: "",
      clubName: "",
      city: "",
      postalCode: "",
      country: "",
      timezone: "", // Laisser vide pour déclencher le placeholder
      clubLogo: undefined,
    },
  });

  // Initialize country from geolocation
  useEffect(() => {
    if (geoInfo?.countryName && !form.getValues("country")) {
      form.setValue("country", geoInfo.countryName);
    }
  }, [geoInfo?.countryName, form]);

  // Watch individual fields using useWatch (React Compiler compatible)
  const firstName = useWatch({ control: form.control, name: "firstName" });
  const lastName = useWatch({ control: form.control, name: "lastName" });
  const phone = useWatch({ control: form.control, name: "phone" });
  const clubName = useWatch({ control: form.control, name: "clubName" });

  // Update preview data when form values change
  useEffect(() => {
    // Use queueMicrotask to avoid synchronous setState in effect
    queueMicrotask(() => {
      setPreviewData((prev) => ({
        ...prev,
        fullName: `${firstName} ${lastName}`.trim(),
        phone,
        clubName,
        // Don't update clubLogo here since it's handled in handleLogoChange
      }));
    });
  }, [firstName, lastName, phone, clubName]);

  // Update right panel with preview
  useEffect(() => {
    setRight(<ClubPreview previewData={previewData} />);
  }, [setRight, previewData]);

  const onSubmit = async (data: Step3FormValues) => {
    setIsLoading(true);
    try {
      // Étape 1: Vérifier que le numéro de téléphone est disponible
      if (data.phone) {
        try {
          const phoneCheckData = await checkPhone({
            params: {
              path: { phone: data.phone },
            },
          });

          if (phoneCheckData.status === "not_available") {
            form.setError("phone", {
              type: "manual",
              message: "Ce numéro de téléphone est déjà utilisé par une autre organisation",
            });
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("Phone check error:", error);
          toast.error("Erreur lors de la vérification du numéro de téléphone");
          setIsLoading(false);
          return;
        }
      }

      // Étape 2: Mettre à jour les informations utilisateur
      await new Promise<void>((resolve, reject) => {
        authClient.updateUser(
          {
            first_name: data.firstName,
            name: data.lastName,
          },
          {
            onError: (updateError) => {
              toast.error("Erreur lors de la mise à jour de l'utilisateur", {
                description: updateError.error.message,
              });
              reject(updateError);
            },
            onSuccess: async () => {
              // Force un refresh de la session pour vider le cache
              await authClient.getSession({
                query: { disableCookieCache: true },
                fetchOptions: {
                  onSuccess: () => resolve(),
                  onError: () => resolve(), // On continue même si le refresh échoue
                },
              });
            },
          }
        );
      });

      // Étape 3: Créer l'organisation avec le numéro de téléphone
      const org = await new Promise<{ data: { id: string } }>((resolve, reject) => {
        authClient.organization.create(
          {
            name: data.clubName,
            slug:
              slugify(data.clubName, {
                lower: true,
                strict: true,
              }) +
              "-" +
              Math.random().toString(36).substring(2, 6),
            city: data.city,
            postal_code: data.postalCode,
            country: data.country,
            timezone: data.timezone,
            phoneNumber: data.phone,
          },
          {
            onError: (orgError) => {
              if (orgError.error.code === "ORGANIZATION_ALREADY_EXISTS") {
                toast.error("Erreur lors de la création de l'organisation", {
                  description: "Une organisation avec ce nom existe déjà",
                });
              } else {
                toast.error("Erreur lors de la création de l'organisation", {
                  description: orgError.error.message,
                });
              }
              reject(orgError);
            },
            onSuccess: (result) => {
              resolve(result);
            },
          }
        );
      });

      if (!org.data) {
        return;
      }

      // Étape 4: Upload de l'image si présente
      if (data.clubLogo) {
        try {
          // validate and compress image
          if (!isValidImageFile(data.clubLogo)) {
            toast.error("Format d'image non supporté", { description: "Veuillez utiliser un fichier JPEG, PNG ou WebP" });
            return;
          }
          const compressedFile = await compressImage(data.clubLogo);

          // get signed upload URL
          const { uploadUrl, publicUrl } = await requestUpload({
            body: { contentType: compressedFile.type, fileSize: compressedFile.size },
          });

          // upload to R2
          const uploadResult = await fetch(uploadUrl, {
            method: "PUT",
            body: compressedFile,
            headers: { "Content-Type": compressedFile.type },
            mode: "cors",
          });

          if (!uploadResult.ok) {
            toast.error("Erreur lors de l'upload de l'image", { description: "Veuillez réessayer" });
            return;
          }

          // Étape 5: Mettre à jour le club avec le logo
          await new Promise<void>((resolve) => {
            authClient.organization.update(
              {
                data: {
                  logo: publicUrl,
                },
                organizationId: org.data.id,
              },
              {
                onError: (updateError) => {
                  console.error("Erreur lors de la mise à jour du logo:", updateError);
                  // On continue même si la mise à jour du logo échoue
                  resolve();
                },
                onSuccess: () => {
                  resolve();
                },
              }
            );
          });
        } catch (error) {
          console.error("Erreur lors de l'upload de l'image:", error);
          // On continue même si l'upload échoue
        }
      }

      // Activer l'organisation (une seule fois, après les éventuels uploads)
      await authClient.organization.setActive({
        organizationId: org.data.id,
      });

      // Succès - passer au step suivant (garder le loader jusqu'à la navigation pour éviter le flash)
      toast.success("Club créé avec succès!");
      await updateOnboardingStep("step4");
      return; // On laisse isLoading à true, le composant sera démonté après la navigation
    } catch (error) {
      console.error("Erreur lors de la création du club:", error);
      toast.error("Erreur lors de la création du club");
      setIsLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewData((prev) => ({
          ...prev,
          clubLogo: event.target?.result as string, // Pour l'aperçu
        }));
      };
      reader.readAsDataURL(file);
      form.setValue("clubLogo", file); // Stocker l'objet File directement
    }
  };

  const handleRemoveLogo = () => {
    setFileName(null);
    setPreviewData((prev) => ({
      ...prev,
      clubLogo: null,
    }));
    form.setValue("clubLogo", undefined); // Mettre à undefined ou null
  };

  const asYouType = new AsYouType({
    defaultCountry: "FR",
    defaultCallingCode: "33",
  });

  return (
    <FormShell
      title="Informations du club"
      subtitle={
        <div className="mt-4">
          <div className="text-sm text-muted-foreground">Renseignez les informations de votre club.</div>
          <div className="text-sm text-muted-foreground">Vous pourrez modifier et ajouter d'autres gérants depuis votre espace.</div>
        </div>
      }
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Form {...form}>
        <div className="grid gap-6">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>
                  Prénom du gérant <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Prénom" {...field} autoComplete="given-name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>
                  Nom du gérant <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Nom" {...field} autoComplete="family-name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>
                  Téléphone du club <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="+33 6 12 34 56 78"
                    autoComplete="tel"
                    {...field}
                    onChange={(e) => {
                      field.onChange(asYouType.input(e.target.value));
                      if (["01", "02", "03", "04", "05", "06", "07", "09"].includes(e.target.value.slice(0, 2))) {
                        // add +33 to the number
                        asYouType.reset();
                        field.onChange(asYouType.input("+33" + e.target.value.slice(1)));
                      }

                      if (e.target.value.length === 0) {
                        asYouType.reset();
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="clubName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>
                  Nom du club <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Nom du club" {...field} autoComplete="organization" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Location fields */}
          <div className="grid gap-4">
            <FormLabel>
              Localisation du club <span className="text-red-500">*</span>
            </FormLabel>

            {/* Composant de localisation */}
            <LocationAutocomplete
              value={{
                city: useWatch({ control: form.control, name: "city" }),
                postalCode: useWatch({ control: form.control, name: "postalCode" }),
                country: useWatch({ control: form.control, name: "country" }),
                timezone: useWatch({ control: form.control, name: "timezone" }),
              }}
              onValueChange={(value) => {
                form.setValue("city", value.city, { shouldValidate: true });
                form.setValue("postalCode", value.postalCode, { shouldValidate: true });
                form.setValue("country", value.country, { shouldValidate: true });
                form.setValue("timezone", value.timezone, { shouldValidate: true });
              }}
              errors={{
                postalCode: form.formState.errors.postalCode?.message,
                city: form.formState.errors.city?.message,
              }}
              isSubmitted={form.formState.isSubmitted}
            />

            {/* Affichage des erreurs pour les autres champs */}
            {form.formState.errors.country && <p className="text-sm text-red-500">{form.formState.errors.country.message}</p>}
            {/* Masquer l'erreur timezone car elle est gérée visuellement dans le composant */}

            {/* Timezone display */}
            <div className="flex justify-end">
              <TimezoneDisplay
                value={useWatch({ control: form.control, name: "timezone" })}
                onValueChange={(value) => form.setValue("timezone", value, { shouldValidate: true })}
                className="text-sm"
                showPlaceholder={!useWatch({ control: form.control, name: "timezone" })} // Afficher le placeholder si pas de valeur explicite
                hasError={!!form.formState.errors.timezone}
                isSubmitted={form.formState.isSubmitted}
              />
            </div>
          </div>{" "}
          <FormField
            control={form.control}
            name="clubLogo"
            render={({ field: { ...fieldProps } }) => (
              <FormItem className="grid gap-2">
                <FormLabel>Logo du club</FormLabel>
                <FormControl>
                  <div className="flex flex-col gap-2">
                    <div
                      className="flex cursor-pointer flex-col items-center justify-center rounded-md border border-gray-200 p-4 hover:bg-gray-50"
                      onClick={() => !fileName && document.getElementById("clubLogo")?.click()}
                    >
                      {fileName ? (
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Upload className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-700">{fileName}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLogo();
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Upload className="mb-2 h-8 w-8 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500">Cliquer pour ajouter une image</p>
                        </>
                      )}
                      <Input
                        id="clubLogo"
                        type="file"
                        accept="image/png, image/jpeg, image/webp"
                        className="hidden"
                        {...fieldProps}
                        onChange={(e) => {
                          fieldProps.onChange(e);
                          handleLogoChange(e);
                        }}
                        value={undefined}
                      />
                    </div>
                  </div>
                </FormControl>
                <FormDescription className="text-xs">Format .png, .jpeg ou .webp et taille maximum de 5 Mo.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="mt-4 w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Création du club...
              </>
            ) : (
              "Créer le club"
            )}
          </Button>
        </div>
      </Form>
    </FormShell>
  );
}
